import React from 'react';
import moment from 'moment';
import _ from 'lodash';
import PropTypes from 'prop-types';
import Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';
import Select from 'components/inputs/Select';
import { ADMIN, SUPER_ADMIN, TENANT_ADMIN, MANAGE_FLEET_RESERVATIONS } from 'components/common/constant';
import { API } from 'api';

export const BUFFER_MINUTES = 10;

const STATUS_META = {
  RESERVED: { label: 'Reserved', className: 'fr-pill--reserved' },
  FLEET_CHARGING: { label: 'Fleet Charging', className: 'fr-pill--charging' },
  COMPLETED: { label: 'Completed', className: 'fr-pill--completed' },
  CANCELLED: { label: 'Cancelled', className: 'fr-pill--cancelled' },
  NO_SHOW: { label: 'No-show', className: 'fr-pill--noshow' },
  RESCHEDULED: { label: 'Rescheduled', className: 'fr-pill--rescheduled' },
};

export const STATUS_FILTER_OPTIONS = [
  { label: 'All statuses', value: '' },
  ..._.map(STATUS_META, (meta, key) => ({ label: meta.label, value: key })),
];

export const TARGET_TYPES = [
  { value: 'STATION', label: 'Entire Station' },
  { value: 'CHARGER', label: 'Specific chargers / connectors' },
];

export const REPEAT_TYPES = [
  { value: 'ONCE', label: 'Once' },
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
];

export const DAY_CHIPS = [
  { value: 1, label: 'M', name: 'Monday' },
  { value: 2, label: 'T', name: 'Tuesday' },
  { value: 3, label: 'W', name: 'Wednesday' },
  { value: 4, label: 'T', name: 'Thursday' },
  { value: 5, label: 'F', name: 'Friday' },
  { value: 6, label: 'S', name: 'Saturday' },
  { value: 0, label: 'S', name: 'Sunday' },
];

export const SCOPES = [
  {
    value: 'THIS_OCCURRENCE',
    label: 'Just one day',
    hint: (day, verb) => `Only ${day} is ${verb}. All other days stay as they are.`,
  },
  {
    value: 'THIS_AND_FOLLOWING',
    label: 'This day and every day after it',
    hint: (day, verb) => `${_.upperFirst(day)} and all later days are ${verb}. Earlier days stay as they are.`,
  },
  {
    value: 'ENTIRE_RESERVATION',
    label: 'All upcoming days',
    hint: (day, verb) => `Every upcoming day in this reservation is ${verb}. Past days are not touched.`,
  },
];

export const HISTORY_META = {
  CREATED: { label: 'Created', dotClass: 'fr-dot--blue' },
  EDITED: { label: 'Edited', dotClass: 'fr-dot--red' },
  RESCHEDULED: { label: 'Rescheduled', dotClass: 'fr-dot--red' },
  CANCELLED: { label: 'Cancelled', dotClass: 'fr-dot--red' },
  STARTED: { label: 'Started', dotClass: 'fr-dot--green' },
  COMPLETED: { label: 'Completed', dotClass: 'fr-dot--gray' },
  NO_SHOW: { label: 'No-show', dotClass: 'fr-dot--orange' },
  PREEMPTED: { label: 'Preempted', dotClass: 'fr-dot--green' },
};

export const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, className: 'fr-pill--completed' };
  return <span className={`fr-pill ${meta.className}`}>{meta.label}</span>;
};

StatusPill.propTypes = { status: PropTypes.string };

export const DateField = ({ value, onChange, min, max, disabled, placeholder = 'Select date' }) => (
  <Datetime
    className="fr-picker"
    value={value ? moment(value, 'YYYY-MM-DD') : ''}
    dateFormat="D MMM YYYY"
    timeFormat={false}
    closeOnSelect
    isValidDate={(day) => (!min || day.isSameOrAfter(min, 'day')) && (!max || day.isSameOrBefore(max, 'day'))}
    onChange={(day) => onChange(moment.isMoment(day) ? day.format('YYYY-MM-DD') : '')}
    inputProps={{ className: 'form-control fr-input', placeholder, disabled }}
  />
);

DateField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.string,
  max: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

const TIME_OPTIONS = _.times(288, (i) => {
  const time = moment.utc(i * 5 * 60000).format('HH:mm');
  return { label: time, value: time };
});

export const TimeField = ({ value, onChange }) => (
  <Select className="mb-0" options={TIME_OPTIONS} value={value} placeholder="--:--" disableSort onChange={(time) => onChange(time || '')} />
);

TimeField.propTypes = { value: PropTypes.string, onChange: PropTypes.func.isRequired };

export const canManageFleetReservations = (profileData, rolePermissionData) =>
  [ADMIN, SUPER_ADMIN, TENANT_ADMIN].includes(_.get(profileData, 'role')) ||
  _.some(_.get(rolePermissionData, '[0].permissions', []), { name: MANAGE_FLEET_RESERVATIONS });

export const formatTime = (value) => {
  if (!value) return '';
  if (/^\d{2}:\d{2}$/.test(value)) return value;
  const m = moment(value);
  return m.isValid() ? m.format('HH:mm') : '';
};

export const formatDate = (value, pattern = 'D MMM') => (value ? moment(value).format(pattern) : '');

export const slotText = (dates, window) => {
  const first = _.first(dates);
  const last = _.last(dates);
  const span = first === last ? formatDate(first, 'D MMM YYYY') : `${formatDate(first)} – ${formatDate(last, 'D MMM YYYY')}`;
  return [span, window].filter(Boolean).join(', ');
};

export const formatDateTime = (value) => formatDate(value, 'D MMM YYYY, HH:mm');

export const formatWindow = (from, to) => {
  const a = formatTime(from);
  const b = formatTime(to);
  if (!a || !b) return '';
  return b <= a ? `${a} - ${b} (next day)` : `${a} - ${b}`;
};

export const isRecurring = (repeatType) => repeatType !== 'ONCE';

export const scheduleErrors = (v) => {
  const errors = {};
  if (!v.startDate) errors.startDate = 'Start date is required';
  if (!v.timeFrom) errors.timeFrom = 'Start time is required';
  if (!v.timeTo) errors.timeTo = 'End time is required';
  if (isRecurring(v.repeatType)) {
    if (!v.endDate) errors.endDate = 'End date is required for a repeating reservation';
    else if (v.startDate && v.endDate < v.startDate) errors.endDate = 'End date must be on or after start date';
  }
  if (v.repeatType === 'WEEKLY' && _.isEmpty(v.daysOfWeek)) errors.daysOfWeek = 'Pick at least one day';
  return errors;
};

const listOccurrenceDates = ({ repeatType, startDate, endDate, daysOfWeek = [] }) => {
  if (!startDate) return [];
  if (repeatType === 'ONCE') return [startDate];
  if (!endDate || endDate < startDate) return [];

  const start = moment(startDate, 'YYYY-MM-DD');
  const end = moment(endDate, 'YYYY-MM-DD');
  const dates = [];

  if (repeatType === 'MONTHLY') {
    const dayOfMonth = start.date();
    for (const month = start.clone().startOf('month'); month.isSameOrBefore(end, 'day'); month.add(1, 'month')) {
      if (dayOfMonth > month.daysInMonth()) continue;
      const date = month.clone().date(dayOfMonth);
      if (date.isSameOrBefore(end, 'day')) dates.push(date.format('YYYY-MM-DD'));
    }
    return dates;
  }

  for (const day = start.clone(); day.isSameOrBefore(end, 'day'); day.add(1, 'day')) {
    if (repeatType === 'DAILY' || daysOfWeek.includes(day.day())) dates.push(day.format('YYYY-MM-DD'));
  }
  return dates;
};

export const describeRepeat = ({ repeatType, startDate, endDate, daysOfWeek = [] }) => {
  const until = `to ${formatDate(endDate)}`;
  switch (repeatType) {
    case 'ONCE':
      return `Once on ${formatDate(startDate)}`;
    case 'DAILY':
      return `Every day ${until}`;
    case 'WEEKLY':
      return `Every ${DAY_CHIPS.filter((chip) => daysOfWeek.includes(chip.value))
        .map((chip) => chip.name)
        .join(', ')} ${until}`;
    case 'MONTHLY':
      return `Monthly on day ${moment(startDate, 'YYYY-MM-DD').date()} ${until}`;
    default:
      return '';
  }
};

export const blockStartOf = (timeFrom) => (timeFrom ? moment(timeFrom, 'HH:mm').subtract(BUFFER_MINUTES, 'minutes').format('HH:mm') : '');

export const scheduleSummary = (v) => {
  const blockedFrom = blockStartOf(v.timeFrom);
  return {
    booked: formatWindow(v.timeFrom, v.timeTo),
    blockedFrom,
    blocked: formatWindow(blockedFrom, v.timeTo),
    repeat: describeRepeat(v),
    occurrences: listOccurrenceDates(v).length,
  };
};

export const emptySelection = (mode = 'STATION') => ({ mode, chargerId: null, connectorId: null });

export const connectorLabel = (connectorId, name) => {
  if (_.isNil(connectorId) || connectorId === '') return name || '';
  return name ? `${connectorId} (${name})` : `${connectorId}`;
};

const findCharger = (tree, chargerId) => _.find(tree, { chargerId });

export const hasSelection = (selection, tree) => {
  if (selection.mode === 'STATION') return tree.some((charger) => charger.isAvailable);
  if (selection.mode === 'CHARGER') return !!selection.chargerId;
  return !!selection.chargerId && !_.isNil(selection.connectorId);
};

export const pruneUnavailable = (selection, tree) => {
  if (selection.mode === 'STATION' || !selection.chargerId) return selection;
  const charger = findCharger(tree, selection.chargerId);
  if (!charger) return emptySelection(selection.mode);
  if (selection.mode === 'CHARGER') return charger.isAvailable ? selection : emptySelection('CHARGER');
  const connector = _.find(charger.connectors, { connectorId: selection.connectorId });
  return _.get(connector, 'isAvailable') ? selection : emptySelection('CONNECTOR');
};

export const describeResources = (selection, tree) => {
  if (selection.mode === 'STATION') {
    const count = tree.filter((charger) => charger.isAvailable).length;
    return [`Entire station (${count} charger${count === 1 ? '' : 's'})`];
  }
  const charger = findCharger(tree, selection.chargerId);
  if (!charger) return [];
  if (selection.mode === 'CHARGER') return [`${charger.ocppId} (whole charger)`];
  const connector = _.find(charger.connectors, { connectorId: selection.connectorId });
  return connector ? [`${charger.ocppId} → ${connectorLabel(connector.connectorId, connector.name)}`] : [];
};

const stationCache = {};
export const loadStations = () => {
  const key = new URLSearchParams(window.location.search).get('tenant') || 'self';
  if (!stationCache[key]) {
    stationCache[key] = API.getStationByCity({})
      .then((res) => asArray(_.get(res, 'data')))
      .catch((error) => {
        delete stationCache[key];
        throw error;
      });
  }
  return stationCache[key];
};

export const toStationOptions = (stations) =>
  _.sortBy(
    _.map(stations, (item) => ({ label: item.name, value: item.id || item._id })),
    'label'
  );

export const unwrap = (body) => (body && _.has(body, 'data') && _.has(body, 'status') ? body.data : body);

export const asArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(_.get(data, 'results'))) return data.results;
  return [];
};

const splitWindow = (window) => {
  const [from = '', to = ''] = String(window || '')
    .split('-')
    .map((part) => part.trim());
  return [from, to];
};

export const buildFleetOptions = (owners = []) =>
  _.flatMap(owners, (owner) => [
    { value: owner.ownerId, label: owner.label || owner.name, fleetOwnerId: owner.ownerId, fleetMemberId: null },
    ..._.get(owner, 'members', []).map((member) => ({
      value: member.memberId,
      label: member.label || member.name,
      fleetOwnerId: owner.ownerId,
      fleetMemberId: member.memberId,
    })),
  ]);

export const conflictText = (conflict) => {
  if (!conflict) return '';
  const date = formatDate(conflict.reservationDate || conflict.blockStartTime, 'D MMM YYYY');
  const window = formatWindow(conflict.blockStartTime, conflict.bookedEndTime);
  if (conflict.conflictType === 'CHARGER_BOOKING') {
    return conflict.status === 'scheduled' ? _.compact([`Customer booking ${date}`, window]).join(', ') : 'Charging session in progress';
  }
  return `${_.compact([`Reserved ${date}`, window]).join(', ')}${conflict.reservationCode ? ` · ${conflict.reservationCode}` : ''}`;
};

export const normalizeAvailability = (raw) =>
  _.get(raw, 'chargers', []).map((charger) => ({
    chargerId: charger.chargerId,
    ocppId: charger.charger_id,
    isAvailable: !!charger.isAvailable,
    conflictLabel: conflictText(charger.conflictDetails),
    connectors: _.get(charger, 'connectors', []).map((connector) => ({
      connectorId: Number(connector.connectorId),
      name: connector.name,
      type: connector.type && connector.type !== 'Unknown' ? connector.type : '',
      power: connector.powerKw ? `${connector.powerKw} kW` : '',
      isAvailable: !!connector.isAvailable,
      conflictLabel: conflictText(connector.conflictDetails) || connector.conflictText,
    })),
  }));

export const normalizeChargerList = (chargers) =>
  chargers.map((charger) => ({
    chargerId: charger._id,
    ocppId: charger.charger_id,
    isAvailable: true,
    connectors: _.get(charger, 'plugs', []).map((plug) => ({
      connectorId: Number(plug.connector_id),
      name: String.fromCharCode(64 + Number(plug.connector_id)),
      type: plug.connector_type || '',
      power: parseFloat(plug.rating) ? `${parseFloat(plug.rating)} kW` : '',
      isAvailable: true,
    })),
  }));

export const normalizeListRow = (row) => {
  const [bookedStartTime, bookedEndTime] = splitWindow(row.bookedWindow);
  return {
    id: row.id,
    reservationCode: row.reservationCode,
    fleetName: row.fleetName,
    memberName: row.fleetMemberName || '',
    stationName: row.stationName,
    targetType: row.targetType,
    chargerOcppId: row.charger_id && row.charger_id !== 'Unknown' ? row.charger_id : '',
    connectorId: row.connectorId,
    connectorName: row.connectorName === 'All' ? '' : row.connectorName,
    reservationDate: row.reservationDate,
    bookedStartTime,
    bookedEndTime,
    blockStartTime: row.blockedFrom,
    status: row.status,
    isActive: !!row.isActive,
  };
};

export const normalizeDetail = (raw = {}) => {
  const reservation = raw.reservationCard || {};
  const resource = raw.resourceCard || {};
  const schedule = raw.scheduleCard || {};
  const charging = raw.chargingCard || {};
  const [bookedStartTime, bookedEndTime] = splitWindow(schedule.bookedWindow);
  const [blockStartTime] = splitWindow(schedule.effectiveBlockedWindow);

  const occurrences = _.get(raw, 'occurrences', []).map((item) => {
    const [from, to] = splitWindow(item.bookedWindow);
    return {
      id: item.rowId,
      reservationDate: item.date,
      chargerOcppId: item.charger_id,
      connectorId: item.connectorId,
      bookedStartTime: from,
      bookedEndTime: to,
      blockStartTime: item.blockedFrom,
      status: item.status,
      isActive: item.isActive,
      actualStartTime: item.actualStartTime,
      actualEndTime: item.actualEndTime,
    };
  });

  return {
    reservationCode: raw.reservationCode,
    status: raw.overallStatus,
    isActive: occurrences.some((item) => item.isActive),
    fleetName: reservation.fleetName,
    memberName: reservation.fleetMemberName || '',
    createdBy: reservation.createdBy,
    createdAt: reservation.createdAt,
    stationName: resource.stationName,
    targetType: resource.targetType,
    resources: _.uniqBy(occurrences, (item) => `${item.chargerOcppId}-${item.connectorId}`).map((item) => ({
      chargerOcppId: item.chargerOcppId,
      connectorId: item.connectorId,
    })),
    repeatType: schedule.repeatType,
    daysOfWeek: schedule.daysOfWeek || [],
    seriesStartDate: schedule.startDate,
    seriesEndDate: schedule.endDate,
    bookedStartTime,
    bookedEndTime,
    bufferMinutes: schedule.bufferMinutes,
    blockStartTime,
    totalOccurrences: schedule.totalOccurrences || occurrences.length,
    charging: {
      sessionId: charging.sessionId,
      actualStartTime: charging.actualStartTime,
      actualEndTime: charging.actualEndTime,
      handover: charging.preemptedSessionId ? { sessionId: charging.preemptedSessionId, stoppedAt: charging.stopDispatchedAt } : null,
    },
    occurrences,
    history: raw.history || [],
  };
};

export const canRescheduleRow = (row) => ['RESERVED', 'RESCHEDULED'].includes(row.status) && row.reservationDate >= moment().format('YYYY-MM-DD');
export const canCancelRow = (row) => !!row.isActive && ['RESERVED', 'RESCHEDULED'].includes(row.status);

export const editableDates = (detail, action = 'reschedule', row) =>
  _.uniq(
    detail.occurrences
      .filter((item) => !row || (item.chargerOcppId === row.chargerOcppId && item.connectorId === row.connectorId))
      .filter(action === 'cancel' ? canCancelRow : canRescheduleRow)
      .map((item) => item.reservationDate)
  ).sort();

const scheduleFields = (schedule) => {
  const body = {
    repeatType: schedule.repeatType,
    startDate: schedule.startDate,
    ...(schedule.repeatType !== 'ONCE' && { endDate: schedule.endDate }),
    timeFrom: schedule.timeFrom,
    timeTo: schedule.timeTo,
  };
  if (schedule.repeatType === 'WEEKLY') body.daysOfWeek = _.sortBy(schedule.daysOfWeek);
  return body;
};

export const buildAvailabilityPayload = (stationId, schedule) => ({ stationId, ...scheduleFields(schedule) });

export const buildCreatePayload = ({ stationId, schedule, fleet, selection, reason }) => {
  const body = { stationId, targetType: selection.mode, fleetOwnerId: fleet.fleetOwnerId, ...scheduleFields(schedule) };
  if (fleet.fleetMemberId) body.fleetMemberId = fleet.fleetMemberId;
  if (reason.trim()) body.reason = reason.trim();
  if (selection.mode !== 'STATION') body.chargerId = selection.chargerId;
  if (selection.mode === 'CONNECTOR') body.connectorId = Number(selection.connectorId);
  return body;
};

export const buildScopePayload = ({ scope, occurrenceDate }) =>
  scope === 'ENTIRE_RESERVATION' ? { scopeOfChange: scope } : { scopeOfChange: scope, occurrenceDate };
